/**
 * «Paquetes»: the pack sizes a product's sales ask for and it does not have (the owner, 2026-10-21:
 * «a tab called "Packages" that suggest missing pack sizes for items that have demonstrated a
 * pattern of selling in specific larger quantities»).
 *
 * A product sold 12 at a time, sale after sale, is a box of 12 rung up as 12 packets: the cashier has
 * to know the box is 12 and count it into the line. With a presentation of 12 it is one tap at the
 * till («Unidad | Paquete (12)», 2026-10-18). On 2026-10-21 the live shop had one product with a
 * second presentation, and 199 of the 1,158 lines it had sold were of exactly 12, of 79 products.
 *
 * What makes an amount a pattern, measured on that shop's sales:
 *
 *   · the same amount of the product, in its base unit, whatever presentation it was rung up in;
 *   · a whole number of its smallest presentation, `MINIMO_PAQUETE` of it or more: two of anything
 *     is two sodas, not a package;
 *   · sold so in `VECES_PAQUETE` sales or more, on `DIAS_PAQUETE` different days or more: a
 *     customer's three sales of one morning are one customer;
 *   · and `SOBRE_LOS_VECINOS` times as often as the amounts either side of it together. Twelve sold
 *     ten times while 11 and 13 never were is a box; 3 lb of beans sold nine times beside 2 lb four
 *     times and 4 lb three is what people ask for. That test is what keeps a staple sold by the
 *     pound off the list at its everyday amounts and on it at its arroba.
 *
 * An amount that is a whole number of a presentation the product has is rung up as that many of it,
 * and asks for nothing. Of the rest, the one sold so most often is the suggestion, one per product;
 * an amount that is a whole number of one sold so more often is that one bought several at a time
 * (24 is two boxes of 12), and is never suggested on its own. A suggestion turned down on a device
 * (`noSugeridos`) gives way to the next amount, if the product has one.
 *
 * Pure, so the screen and its tests share one answer.
 */

import { saleToBase, sumarDias, type Milli } from '@abastece/shared';
import type { Producto, UnidadVenta, Venta } from '@abastece/core';

/** How far back the sales are read: a quarter, so a box sold twice a month shows. */
export const DIAS_DE_PAQUETES = 90;

/** Sales of exactly that amount, at the least. */
export const VECES_PAQUETE = 3;

/** On this many different days, at the least. */
export const DIAS_PAQUETE = 2;

/** The least a package holds, in the product's smallest presentation. */
export const MINIMO_PAQUETE = 3;

/** How many times as often as the amounts either side of it, together. */
export const SOBRE_LOS_VECINOS = 2;

export interface PaqueteSugerido {
  productoId: string;
  /** What it would hold, in base units (milli): 12 packets are 12000. */
  factor: Milli;
  /** Sales of exactly that much. */
  ventas: number;
  /** The different days they were on. */
  dias: number;
  /** Every sale of the product in the window, so a row can say «10 of its 22». */
  deTodas: number;
  /** What it starts called (`nombreDePaquete`). */
  nombre: string;
}

/** A suggestion's name on a device's list of those turned down. */
export const claveDePaquete = (productoId: string, factor: number): string => `${productoId}:${factor}`;

/**
 * What a new package starts called: the trade's own measures where a staple sold by the pound comes
 * in one, the arroba of 25 and the quintal of 100 (`UNIT_PRESETS`), a bag for any other weight, and a
 * pack for the rest. A word the screens say in either language (`etiquetaUnidad`), changed in the
 * sheet before it is added.
 */
export function nombreDePaquete(unidadBase: string, factor: number): string {
  if (unidadBase === 'libra') {
    if (factor === 25_000) return 'arroba';
    if (factor === 100_000) return 'quintal';
    return 'bolsa';
  }
  return 'paquete';
}

interface Cantidad {
  ventas: Set<string>;
  dias: Set<string>;
}

/** The suggestions, one per product on sale at most, the amount sold so most often first. */
export function paquetesSugeridos(d: {
  productos: readonly Producto[];
  unidades: readonly UnidadVenta[];
  ventas: readonly Venta[];
  hoy: string;
  /** `claveDePaquete` of each suggestion turned down on this device. */
  noSugeridos?: ReadonlySet<string>;
}): PaqueteSugerido[] {
  const desde = sumarDias(d.hoy, -(DIAS_DE_PAQUETES - 1));
  const vendido = new Map<string, { ventas: Set<string>; porCantidad: Map<number, Cantidad> }>();
  for (const v of d.ventas) {
    if (v.anulada || v.fecha < desde || v.fecha > d.hoy) continue;
    for (const l of v.lineas) {
      if (!l.productoId) continue;
      const cantidad = saleToBase(l.cantidad, l.factor);
      const suyo = vendido.get(l.productoId) ?? { ventas: new Set<string>(), porCantidad: new Map<number, Cantidad>() };
      const asi = suyo.porCantidad.get(cantidad) ?? { ventas: new Set<string>(), dias: new Set<string>() };
      suyo.ventas.add(v.id);
      asi.ventas.add(v.id);
      asi.dias.add(v.fecha);
      suyo.porCantidad.set(cantidad, asi);
      vendido.set(l.productoId, suyo);
    }
  }

  const sugeridos: PaqueteSugerido[] = [];
  for (const p of d.productos) {
    const suyo = vendido.get(p.id);
    if (!p.activo || !suyo) continue;
    const suyas = d.unidades.filter((u) => u.productoId === p.id).sort((a, b) => a.factor - b.factor);
    const menor = suyas[0]?.factor;
    if (!menor || menor <= 0) continue;
    const veces = (cantidad: number) => suyo.porCantidad.get(cantidad)?.ventas.size ?? 0;
    const patrones = [...suyo.porCantidad]
      .filter(
        ([cantidad, asi]) =>
          cantidad % menor === 0 &&
          cantidad >= MINIMO_PAQUETE * menor &&
          !suyas.some((u) => u.factor > menor && cantidad % u.factor === 0) &&
          asi.ventas.size >= VECES_PAQUETE &&
          asi.dias.size >= DIAS_PAQUETE &&
          asi.ventas.size >= SOBRE_LOS_VECINOS * (veces(cantidad - menor) + veces(cantidad + menor)),
      )
      .sort(([ca, a], [cb, b]) => b.ventas.size - a.ventas.size || b.dias.size - a.dias.size || ca - cb);
    // Two boxes of 12 are not a box of 24: an amount a stronger one goes into whole is that one.
    const propios = patrones.filter(([cantidad], i) => !patrones.slice(0, i).some(([antes]) => cantidad % antes === 0));
    const elegido = propios.find(([cantidad]) => !d.noSugeridos?.has(claveDePaquete(p.id, cantidad)));
    if (!elegido) continue;
    const [factor, asi] = elegido;
    sugeridos.push({
      productoId: p.id,
      factor: factor as Milli,
      ventas: asi.ventas.size,
      dias: asi.dias.size,
      deTodas: suyo.ventas.size,
      nombre: nombreDePaquete(p.unidadBase, factor),
    });
  }
  return sugeridos.sort((a, b) => b.ventas - a.ventas || b.dias - a.dias || (a.productoId < b.productoId ? -1 : a.productoId > b.productoId ? 1 : 0));
}
